/**
 * Navigation and bounds calculation utilities for canvas interactions
 * Extracted from MyCanvas.js to reduce file size
 */

import { sanitizeId } from "./textDataUtils";

/** Count drawable entities in canvasData for return-path diagnostics */
function countCanvasEntities(canvasData) {
  if (!canvasData || typeof canvasData !== "object") return 0;
  const keys = [
    "texts",
    "rectangles",
    "circles",
    "arrows",
    "lines",
    "doubleSidedArrows",
    "scribbles",
    "images",
    "videos",
  ];
  return keys.reduce(
    (n, k) => n + (Array.isArray(canvasData[k]) ? canvasData[k].length : 0),
    0,
  );
}

/**
 * When leaving MyCanvas, router `location.state.canvasPages` can be stale (e.g. only
 * pages that existed on an earlier visit). `handleNavigateToCanvas` always writes the
 * full lightweight list to localStorage right before opening MyCanvas — merge with that
 * so add-mode users do not lose newly added pages on return.
 */
const pageKeyForCanvas = (p) => {
  if (!p) return null;
  const k = p.id ?? p.pageId;
  return k != null && String(k).trim() !== "" ? String(k) : null;
};

/** Exported for CanvasForm add-mode return sync (same union logic as leaving MyCanvas). */
export const mergeCanvasPagesForReturn = (statePages, storedPagesRaw) => {
  const stateArr = Array.isArray(statePages) ? statePages : [];
  let storedArr = [];
  if (typeof storedPagesRaw === "string" && storedPagesRaw && storedPagesRaw !== "undefined") {
    try {
      const parsed = JSON.parse(storedPagesRaw);
      if (Array.isArray(parsed)) storedArr = parsed;
    } catch {
      // ignore
    }
  }
  const byId = new Map();
  storedArr.forEach((p) => {
    const k = pageKeyForCanvas(p);
    if (k) {
      byId.set(k, { ...p, id: p.id || p.pageId });
    }
  });
  stateArr.forEach((p) => {
    const k = pageKeyForCanvas(p);
    if (!k) return;
    const prev = byId.get(k) || {};
    byId.set(k, { ...prev, ...p, id: p.id || p.pageId || prev.id });
  });
  const seen = new Set();
  const out = [];
  stateArr.forEach((p) => {
    const k = pageKeyForCanvas(p);
    if (k && byId.has(k) && !seen.has(k)) {
      seen.add(k);
      out.push(byId.get(k));
    }
  });
  storedArr.forEach((p) => {
    const k = pageKeyForCanvas(p);
    if (k && byId.has(k) && !seen.has(k)) {
      seen.add(k);
      out.push(byId.get(k));
    }
  });
  return out;
};

/**
 * Navigates back with canvas state preservation
 * @param {Object} snapshot - Canvas snapshot
 * @param {Object} params - Parameters containing elementCounts and canvasData
 * @param {string} pageId - Current page ID
 * @param {Function} openCanvasDB - Function to open IndexedDB
 * @param {Object} removedShapeIds - Object containing removed shape IDs
 * @param {Object} location - Location object from React Router
 * @param {Function} navigate - Navigation function from React Router
 */
export const navigateBack = (
  snapshot,
  { elementCounts, canvasData },
  pageId,
  openCanvasDB,
  removedShapeIds,
  location,
  navigate,
  hasChanges = true
) => {
  // Save removed shape IDs and current canvasData to IndexedDB before navigating.
  // canvasData must be persisted so CanvasForm's handleConfirmSave finds it when loading by pageId.
  const saveToIndexedDB = async () => {
    try {
      const db = await openCanvasDB();
      const transaction = db.transaction(["canvasElements"], "readwrite");
      const store = transaction.objectStore("canvasElements");

      // Save removed shape IDs
      const removedShapeIdsData = {
        removedRectangles: removedShapeIds.removedRectangles,
        removedCircles: removedShapeIds.removedCircles,
        removedArrows: removedShapeIds.removedArrows,
        removedDoubleSidedArrows: removedShapeIds.removedDoubleSidedArrows,
        removedScribbles: removedShapeIds.removedScribbles,
        removedTexts: removedShapeIds.removedTexts,
        removedImages: removedShapeIds.removedImages,
        removedVideos: removedShapeIds.removedVideos,
        removedLines: removedShapeIds.removedLines,
        removedOffCanvasRectangles: removedShapeIds.removedOffCanvasRectangles,
        removedOffCanvasCircles: removedShapeIds.removedOffCanvasCircles,
        removedOffCanvasArrows: removedShapeIds.removedOffCanvasArrows,
        removedOffCanvasDoubleSidedArrows:
          removedShapeIds.removedOffCanvasDoubleSidedArrows,
        removedOffCanvasScribbles: removedShapeIds.removedOffCanvasScribbles,
        removedOffCanvasTexts: removedShapeIds.removedOffCanvasTexts,
        removedOffCanvasImages: removedShapeIds.removedOffCanvasImages,
        removedOffCanvasVideos: removedShapeIds.removedOffCanvasVideos,
        removedOffCanvasLines: removedShapeIds.removedOffCanvasLines,
        removedPartReferences: removedShapeIds.removedPartReferences || [],
      };
      await store.put({
        id: `${pageId}_removed_shapes`,
        data: removedShapeIdsData,
        lastModified: Date.now(),
      });

      // Persist current elements (including user-added text) so handleConfirmSave gets them
      if (canvasData && pageId) {
        const key = sanitizeId(pageId, pageId);
        const textCount = canvasData.texts?.length ?? 0;
        const userTexts = (canvasData.texts || []).filter((t) => t.createdOnCanvas === true);
        // IMPORTANT: Leave flow already persists canonical page payload via saveUtils.
        // Do not overwrite an existing row here, otherwise stale navigation payload can
        // resurrect deleted elements (observed for off-canvas texts).
        const existingRow = await new Promise((resolve, reject) => {
          const req = store.get(key);
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error);
        });
        if (!existingRow?.data) {
          await store.put({
            id: key,
            data: canvasData,
            lastModified: new Date().toISOString(),
          });
        } else {
        }
      }
    } catch (error) {
    }
  };

  // Run save then navigate so payload has latest elements when user saves from form
  saveToIndexedDB().then(() => {
    doNavigate();
  });

  function doNavigate() {

  // Prefer full page list: location state can be stale; localStorage was updated in
  // handleNavigateToCanvas right before this MyCanvas session with all pages.
  const stateCanvasPages = location?.state?.canvasPages || [];
  const storedPagesRaw =
    (typeof localStorage !== "undefined" &&
      (localStorage.getItem("canvasPages") ||
        localStorage.getItem("originalCanvasPages") ||
        localStorage.getItem("canvas_pages"))) ||
    null;
  const sessionPagesRaw =
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("lastCanvasFormPagesBeforeMyCanvas")
      : null;
  // Chain merges: router state can be stale; localStorage can be overwritten; sessionStorage
  // is set synchronously in handleNavigateToCanvas with the full page list before opening MyCanvas.
  let originalCanvasPages = mergeCanvasPagesForReturn(
    stateCanvasPages,
    storedPagesRaw,
  );
  originalCanvasPages = mergeCanvasPagesForReturn(
    originalCanvasPages,
    sessionPagesRaw,
  );

  const state = {
    snapshot,
    hasChanges: hasChanges, // Pass the actual hasChanges value (can be false)
    pageId,
    fromMyCanvas: true,
    elementCounts,
    canvasData, // Include the actual canvas data
    canvasPages: originalCanvasPages, // Preserve the original canvas pages
    // Preserve "where to go next" intent set by ViewStep/ViewSubstep/ViewChildNode.
    // This is consumed by CanvasForm's Back button via `location.state.returnTo`.
    returnTo: location?.state?.returnTo,
  };

  // CRITICAL: Only set fromMyCanvas to "true" in localStorage if hasChanges is true
  // If hasChanges is false, we should NOT trigger the blocker
  // ALSO: Set persistent flag for this canvas to persist across MyCanvas visits
  // Use canvas ID (from localStorage) instead of pageId for the persistent flag key
  const canvasId = localStorage.getItem("currentCanvasId") || pageId; // Fallback to pageId if canvasId not found
  const persistentFlagKey = `canvasHasUnsavedChanges_${canvasId}`;
  const persistedEntityCount = countCanvasEntities(canvasData);
  // Add-canvas-template reload reads localStorage.fromMyCanvas + pageId. If deep-compare says
  // hasChanges=false but canvasData still has shapes, we must still set the flag or IndexedDB merge never runs.
  const shouldSetFromMyCanvasFlag = hasChanges || persistedEntityCount > 0;

  if (hasChanges) {
    localStorage.setItem("fromMyCanvas", "true");
    // CRITICAL: Set persistent flag so changes persist across multiple MyCanvas visits
    // This flag will only be cleared when user saves or goes back to ViewChildNode
    localStorage.setItem(persistentFlagKey, "true");
  } else if (shouldSetFromMyCanvasFlag) {
    localStorage.setItem("fromMyCanvas", "true");
  } else {
    // DON'T remove persistent flag here - only remove when user saves or goes back to ViewChildNode
    // This ensures changes persist across visits even if user doesn't make changes on subsequent visits
    // Remove it if it exists, so we don't trigger false positives
    localStorage.removeItem("fromMyCanvas");
  }


  localStorage.setItem("pageId", pageId);
  localStorage.setItem("elementCounts", JSON.stringify(elementCounts));


  // CRITICAL: Don't save full page objects to localStorage - causes quota exceeded errors
  // Only save lightweight metadata, snapshots are stored in IndexedDB
  try {
    const lightweightPages = (originalCanvasPages || []).map((p) => ({
      id: p?.id || p?.pageId,
      canvas_page_id: p?.canvas_page_id, // Preserve canvas_page_id if exists
      zoomLevel: typeof p?.zoomLevel === "number" ? p.zoomLevel : 100,
      lastModified: p?.lastModified || new Date().toISOString(),
      canvasHeight: p?.canvasHeight,
      newlyAdded: p?.newlyAdded,
      // snapshot: p?.snapshot, // REMOVED - Snapshots stored in IndexedDB only
      shortDescription: p?.shortDescription,
      templateName: p?.templateName,
      canva_snap_url: p?.canva_snap_url,
      templateApplied: p?.templateApplied,
      text_boxes: Array.isArray(p?.text_boxes) ? p.text_boxes : [],
      text_box_content_data: Array.isArray(p?.text_box_content_data)
        ? p.text_box_content_data
        : [],
    }));

    // CRITICAL: Save to both "canvasPages" and "originalCanvasPages" for compatibility
    // useCanvasPersistence looks for "canvasPages" when returning from MyCanvas
    localStorage.setItem(
      "canvasPages",
      JSON.stringify(lightweightPages)
    );
    localStorage.setItem(
      "originalCanvasPages",
      JSON.stringify(lightweightPages)
    );
    
  } catch (error) {
    console.error(
      "Error saving pages to localStorage (quota exceeded):",
      error
    );
    // Continue navigation even if localStorage save fails - pages are in state
  }

  // Prefer explicit returnPath from CanvasForm when available, otherwise fall back to history back
  const returnPath = location?.state?.returnPath;
  if (returnPath) {
    try {
      // Use SPA navigation so router `state` is preserved (including `returnTo`).
      navigate(returnPath, { state, replace: true });
    } catch {
      // Absolute fallback: force a full navigation.
      const origin = window.location.origin || "";
      const currentPath = window.location.pathname || "";
      const idx = currentPath.indexOf("/canvas");
      const basePrefix = idx >= 0 ? currentPath.slice(0, idx) : "";

      if (/^https?:\/\//i.test(returnPath)) {
        window.location.assign(returnPath);
        return;
      }

      const normalizedBasePrefix = basePrefix.replace(/\/$/, "");
      const normalizedReturnPath = returnPath.startsWith("/")
        ? returnPath
        : `/${returnPath}`;

      const fullPath =
        normalizedBasePrefix &&
        normalizedReturnPath.startsWith(normalizedBasePrefix)
          ? `${origin}${normalizedReturnPath}`
          : `${origin}${normalizedBasePrefix}${normalizedReturnPath}`;

      window.location.assign(fullPath);
    }
  } else {
    navigate(-1, { state });
  }
  }
}

/**
 * Deduplicates array items by marker_id or id
 * @param {Array} items - Array to deduplicate
 * @returns {Array} Deduplicated array
 */
export const deduplicate = (items) => {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.filter((item) => {
    const key = item?.marker_id || item?.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Calculates drawing bounds for canvas elements
 * @param {Object} stageRef - Reference to Konva stage
 * @returns {Object} Bounds object with minX, minY, width, height
 */
export const calculateDrawingBounds = (stageRef) => {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  let hasShapes = false;

  // Get all drawable elements
  const shapes = stageRef.current.find("Shape");
  const groups = stageRef.current.find("Group");
  const allElements = [...shapes, ...groups];

  allElements.forEach((element) => {
    const rect = element.getClientRect({ relativeTo: stageRef.current });
    if (!rect || rect.width === 0 || rect.height === 0) return;

    hasShapes = true;
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.width);
    maxY = Math.max(maxY, rect.y + rect.height);
  });

  // Fallback to stage dimensions if no shapes found
  if (!hasShapes || !isFinite(minX)) {
    return {
      minX: 0,
      minY: 0,
      width: stageRef.current.width(),
      height: stageRef.current.height(),
    };
  }

  // Add padding
  const padding = 20;
  return {
    minX: Math.max(0, minX - padding),
    minY: Math.max(0, minY - padding),
    width: Math.max(1, maxX - minX + 2 * padding),
    height: Math.max(1, maxY - minY + 2 * padding),
  };
};
